import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { StatusApi } from "@/lib/apiClient";
import { Warehouse, Store, Briefcase, ArrowRight, ArrowUpRight, Phone, Mail, MapPin, Check, Compass, Home } from "lucide-react";

// SIACASA brand palette (from logo)
const ACCENT = "#6E93C7";   // blu casetta
const BG = "#3A3A3C";       // antracite (sfondo logo)
const BG_DARK = "#2E2E30";  // antracite scuro per profondità
const BORDER = "rgba(255,255,255,0.12)";
const LOGO = "/siacasa-logo.png";
const MAP = "https://static.prod-images.emergentagent.com/jobs/2bbe7f3d-cc6f-4767-a51a-aa70c32c59e8/images/b47ab638870a4fd5c1bfc887fb00c82c64541ae9a84b510e9d2e79d829ffec14.png";

const IMG = {
  hero: "/hero-siacasa.jpg",
  me: "/stefano.png",
  warehouse: "https://images.unsplash.com/photo-1674252281682-2eec258cfa30?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  office: "https://images.unsplash.com/photo-1579487785973-74d2ca7abdd5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  retail: "https://images.unsplash.com/photo-1612124119733-d23933139790?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const fadeUp = {
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const Overline = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-[11px] sm:text-xs uppercase tracking-[0.3em] font-bold" style={{ color: ACCENT }}>
    <span className="h-px w-6" style={{ background: ACCENT }} />
    <span className="text-white/60">{children}</span>
  </span>
);

const Logo = ({ className = "h-9" }) => (
  <img src={LOGO} alt="SIACASA — La direzione giusta" className={`${className} w-auto`} data-testid="landing-logo" />
);

export default function LandingPage() {
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", interest: "", message: "", privacy: false });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [company, setCompany] = useState("");
  const [promos, setPromos] = useState([]);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("t") || "";
    StatusApi.trackVisit(token).then((r) => {
      if (r?.company_name) setCompany(r.company_name);
      if (Array.isArray(r?.promos)) setPromos(r.promos);
    });
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Inserisci nome e cognome");
      return;
    }
    if (!form.email.trim() || !form.email.includes("@")) {
      toast.error("Inserisci un indirizzo email valido: mi serve per poterti rispondere");
      return;
    }
    if (!form.privacy) {
      toast.error("Accetta l'informativa privacy per inviare la richiesta");
      return;
    }
    setSending(true);
    try {
      const token = new URLSearchParams(window.location.search).get("t") || "";
      await StatusApi.submitFunnelContact({ ...form, token, privacy_consent: true });
      setSent(true);
      toast.success("Richiesta inviata! Ti ricontatto personalmente a breve.");
    } catch (err) {
      toast.error("Errore nell'invio. Riprova.");
    } finally {
      setSending(false);
    }
  };

  const scrollToForm = () => document.getElementById("contatti")?.scrollIntoView({ behavior: "smooth" });

  const services = [
    { icon: Warehouse, title: "Capannoni & Logistica", desc: "Vendita e locazione di capannoni, magazzini e poli logistici. Il cuore del mio lavoro.", img: IMG.warehouse, span: "md:col-span-7 md:row-span-2" },
    { icon: Briefcase, title: "Uffici Direzionali", desc: "Spazi ufficio rappresentativi in zone strategiche tra Brianza e Milano.", img: IMG.office, span: "md:col-span-5" },
    { icon: Store, title: "Negozi & Retail", desc: "Locali commerciali ad alto passaggio per la tua attività.", img: IMG.retail, span: "md:col-span-5" },
  ];

  const method = [
    { n: "01", t: "Valutazione reale", d: "Analizzo il tuo immobile con i numeri veri del mercato commerciale, non stime gonfiate per accaparrarmi l'incarico." },
    { n: "02", t: "Marketing B2B mirato", d: "Non aspetto la vetrina: porto il tuo immobile davanti ad aziende e investitori realmente interessati." },
    { n: "03", t: "Trattativa riservata", d: "Gestisco personalmente ogni contatto e ogni offerta, con la massima riservatezza." },
    { n: "04", t: "Chiusura sicura", d: "Ti seguo fino al rogito, con SIACASA come struttura solida alle spalle." },
  ];

  return (
    <div className="min-h-screen text-white antialiased" style={{ background: BG, fontFamily: "'Avenir Next', 'Segoe UI', Helvetica, Arial, sans-serif" }}>
      <Toaster position="top-center" />

      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl border-b" style={{ background: `${BG}D9`, borderColor: BORDER }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <Button onClick={scrollToForm} data-testid="landing-header-cta"
            className="rounded-none h-9 px-5 font-semibold text-white transition-transform active:scale-95 hover:brightness-110"
            style={{ background: ACCENT }}>
            Contattami
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 sm:pt-32 pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <motion.div {...fadeUp} className="lg:col-span-6">
            <Overline>{company ? `Lettera riservata a ${company}` : "Riservato alle imprenditrici e agli imprenditori"}</Overline>
            <h1 className="font-display text-[2.6rem] leading-[0.95] sm:text-6xl lg:text-[4.4rem] font-black tracking-tight mt-6">
              Il valore del tuo immobile d'impresa richiede <span className="relative"><span className="relative z-10">una chiave di lettura</span><span className="absolute left-0 -bottom-1 h-1.5 w-full" style={{ background: ACCENT }} /></span> diversa.
            </h1>
            <p className="text-base sm:text-lg leading-relaxed text-white/70 mt-7 max-w-xl">
              Un capannone, un ufficio o un negozio non si valutano come una casa: dinamiche di
              mercato, criteri e acquirenti sono completamente diversi. Dal <strong className="text-white">2007</strong> mi
              occupo <strong className="text-white">esclusivamente</strong> di immobili per l'impresa.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button onClick={scrollToForm} data-testid="landing-hero-cta"
                className="rounded-none h-12 px-7 text-base font-semibold text-white transition-transform active:scale-95 hover:brightness-110"
                style={{ background: ACCENT }}>
                Valuta il tuo immobile <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <a href="#metodo"
                className="inline-flex items-center h-12 px-6 rounded-none border text-sm font-semibold text-white hover:bg-white hover:text-[#3A3A3C] transition-colors" style={{ borderColor: "rgba(255,255,255,0.25)" }}>
                Come lavoro
              </a>
            </div>
          </motion.div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -top-3 -left-3 h-full w-full border" style={{ borderColor: ACCENT }} />
              <img src={IMG.hero} alt="Immobile commerciale" className="relative w-full h-[300px] sm:h-[440px] object-cover grayscale-[20%]" />
            </div>
          </motion.div>
        </div>

        {/* fact strip */}
        <div className="mt-14 border-y" style={{ background: BG_DARK, borderColor: BORDER }}>
          <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-3 divide-x" style={{ borderColor: BORDER }}>
            {[["Dal 1994", "SIACASA sul territorio"], ["Dal 2007", "solo commerciale"], ["20+ anni", "di esperienza"]].map(([a, b], i) => (
              <div key={i} className="py-6 px-4 text-center" style={{ borderColor: BORDER }}>
                <div className="font-display text-2xl sm:text-3xl font-black tracking-tight" style={{ color: i === 1 ? ACCENT : "#fff" }}>{a}</div>
                <div className="text-[11px] uppercase tracking-widest text-white/45 mt-1">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Immobili proposti (dalla campagna) */}
      {promos.length > 0 && (
        <section id="immobili" className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20" data-testid="landing-promos">
          <div className="text-center mb-10">
            <Overline>{promos.length > 1 ? "Immobili proposti" : "Immobile proposto"}</Overline>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-4">
              {promos.length > 1 ? `${promos.length} immobili selezionati per lei` : "Una proposta per lei"}
            </h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
              Di seguito i dettagli. Per l'annuncio completo e tutte le foto clicchi su "Per maggiori informazioni".
            </p>
          </div>
          <div className={`grid gap-6 ${promos.length > 1 ? "md:grid-cols-2 lg:grid-cols-3" : "max-w-2xl mx-auto"}`}>
            {promos.map((p, i) => (
              <div key={i} data-testid={`landing-promo-${i}`}
                className="border flex flex-col overflow-hidden transition-transform hover:-translate-y-1"
                style={{ borderColor: "#e5e5e5", background: "#ffffff" }}>
                {p.image ? (
                  <img src={p.image} alt={p.title || "Immobile"} className="w-full h-52 object-cover"
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = IMG.hero; }} />
                ) : (
                  <div className="w-full h-52 flex items-center justify-center bg-neutral-100 text-neutral-400">
                    <Warehouse className="h-10 w-10" />
                  </div>
                )}
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-display text-xl font-bold leading-tight" style={{ color: "#1a1a1a" }}>{p.title || "Immobile commerciale"}</h3>
                  {p.price && (
                    <div className="inline-block mt-3 self-start text-white text-sm font-bold px-3 py-1" style={{ background: ACCENT }}>{p.price}</div>
                  )}
                  {p.description && <p className="text-sm mt-3 line-clamp-4 flex-1" style={{ color: "#555" }}>{p.description}</p>}
                  {p.source_url && (
                    <a href={p.source_url} target="_blank" rel="noopener noreferrer" data-testid={`landing-promo-link-${i}`}
                      onClick={() => StatusApi.trackClick(new URLSearchParams(window.location.search).get("t") || "", p.source_url)}
                      className="inline-flex items-center justify-center h-11 mt-5 px-5 text-sm font-semibold text-white rounded-none transition-transform active:scale-95 hover:brightness-110"
                      style={{ background: BG_DARK }}>
                      Per maggiori informazioni <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section id="chisono" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <motion.div {...fadeUp} className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative">
            <div className="absolute -bottom-3 -right-3 h-full w-full" style={{ background: ACCENT }} />
            <img src={IMG.me} alt="Geom. Stefano Tronca" className="relative w-full h-[340px] sm:h-[440px] object-cover object-top" />
          </div>
        </motion.div>
        <motion.div {...fadeUp} className="lg:col-span-7 order-1 lg:order-2">
          <Overline>Chi sono</Overline>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-5">
            Geom. Stefano Tronca,<br />Titolare di SIACASA.
          </h2>
          <p className="text-white/70 mt-5 leading-relaxed text-base sm:text-lg max-w-2xl">
            SIACASA è presente sul territorio dal <strong className="text-white">1994</strong>. Dal <strong className="text-white">2007</strong> ho
            scelto di dedicarmi <strong className="text-white">esclusivamente</strong> agli immobili per l'impresa — capannoni, uffici e negozi —
            maturando una profonda conoscenza del mercato non residenziale.
          </p>
          <p className="text-white/70 mt-4 leading-relaxed text-base sm:text-lg max-w-2xl">
            Oggi la domanda di immobili commerciali supera spesso l'offerta: conoscere il <strong className="text-white">valore reale</strong> del
            tuo immobile ti dice se questa è l'occasione giusta per valorizzarlo. Ti seguo personalmente, in modo riservato, fino al rogito.
          </p>
          <ul className="mt-7 grid sm:grid-cols-2 gap-3">
            {["Specializzazione ed esperienza consolidata nel commerciale", "Rete di aziende e investitori qualificati", "Valutazioni asseverate secondo valori di mercato attuali", "Riservatezza totale sulle trattative"].map((t) => (
              <li key={t} className="flex items-start gap-2.5 text-white/85">
                <Check className="h-5 w-5 shrink-0 mt-0.5" style={{ color: ACCENT }} /> <span className="text-sm sm:text-base">{t}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </section>

      {/* Servizi - Bento */}
      <section id="servizi" style={{ background: BG_DARK }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <motion.div {...fadeUp}>
            <Overline>Cosa tratto</Overline>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-4 max-w-2xl">
              Immobili commerciali, prima di tutto.
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-12 auto-rows-[210px]">
            {services.map((s, i) => (
              <motion.div key={s.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                data-testid={`service-card-${i}`}
                className={`group relative overflow-hidden border ${s.span}`} style={{ borderColor: BORDER }}>
                <img src={s.img} alt={s.title} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${BG_DARK}, ${BG_DARK}88 40%, transparent)` }} />
                <div className="relative h-full p-6 flex flex-col justify-end">
                  <s.icon className="h-7 w-7 mb-3" style={{ color: ACCENT }} />
                  <h3 className="font-display text-2xl font-bold tracking-tight">{s.title}</h3>
                  <p className="text-sm text-white/70 mt-1.5 max-w-md">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
          {/* residential note */}
          <motion.div {...fadeUp} className="mt-4 border p-6 flex items-start gap-4" style={{ borderColor: BORDER }}>
            <Home className="h-6 w-6 shrink-0 mt-0.5" style={{ color: ACCENT }} />
            <p className="text-sm sm:text-base text-white/70">
              <strong className="text-white">E per il residenziale?</strong> Ce ne occupiamo con altrettanta cura e professionalità, attraverso un team dedicato specializzato nella divisione residenziale. Pertanto, se hai una casa da vendere, sei comunque nelle mani giuste!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Zona di lavoro */}
      <section id="zona" style={{ background: BG }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <motion.div {...fadeUp}>
            <Overline>Zona di lavoro</Overline>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-4">
              Città e province di Milano e Monza Brianza.
            </h2>
            <p className="text-white/70 mt-5 leading-relaxed text-base sm:text-lg max-w-xl">
              Opero soprattutto nelle città e province di Milano e Monza Brianza, dove conosco
              ogni zona industriale e commerciale: per questo so davvero quanto vale il tuo immobile
              e a chi proporlo.
            </p>
            <p className="text-white/60 mt-4 leading-relaxed text-base max-w-xl">
              Pensi che la tua località sia fuori zona? <span className="text-white font-semibold">Chiedi lo stesso.</span>{" "}
              Preferisco essere io a dirti di no: spesso l'occasione giusta vale il viaggio.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Città e Provincia di Milano", "Città e Provincia di Monza Brianza"].map((z) => (
                <span key={z} className="text-xs font-semibold px-3 py-1.5 border text-white" style={{ borderColor: BORDER }}>{z}</span>
              ))}
            </div>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}>
            <div className="relative border" style={{ borderColor: BORDER }}>
              <img src={MAP} alt="Zona di lavoro: Brianza e Milano" className="w-full h-auto object-cover" data-testid="zona-map" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Metodo */}
      <section id="metodo" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <motion.div {...fadeUp}>
          <Overline>Come lavoro</Overline>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-4 max-w-2xl">
            Un metodo chiaro. La direzione giusta.
          </h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {method.map((m, i) => (
            <motion.div key={m.n} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="border p-6 transition-colors" style={{ borderColor: BORDER, background: BG_DARK }}>
              <div className="font-display text-4xl font-black tracking-tighter" style={{ color: ACCENT }}>{m.n}</div>
              <h3 className="font-display text-xl font-bold tracking-tight mt-4">{m.t}</h3>
              <p className="text-sm text-white/55 mt-2 leading-relaxed">{m.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Funnel / Contact form */}
      <section id="contatti" style={{ background: BG_DARK }}>
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <motion.div {...fadeUp} className="text-center">
            <div className="flex justify-center"><Overline>Il primo passo</Overline></div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-4">Parliamo del tuo immobile</h2>
            <p className="text-white/60 mt-4">Ti offro una prima <strong className="text-white">analisi del valore</strong> del tuo immobile, riservata e non vincolante. Niente spam: i tuoi dati non verranno mai ceduti a terzi e li useremo solo per rispondere alla tua richiesta.</p>
          </motion.div>

          {sent ? (
            <motion.div {...fadeUp} data-testid="funnel-success"
              className="mt-10 border p-10 text-center" style={{ borderColor: ACCENT, background: BG }}>
              <div className="mx-auto h-14 w-14 flex items-center justify-center rounded-full" style={{ background: ACCENT }}>
                <Check className="h-7 w-7 text-white" />
              </div>
              <h3 className="font-display text-2xl font-bold mt-5">Richiesta ricevuta!</h3>
              <p className="text-white/60 mt-2">Grazie {form.name || ""}. Ti contatto al più presto.</p>
            </motion.div>
          ) : (
            <motion.form {...fadeUp} onSubmit={submit} data-testid="funnel-form"
              className="mt-10 border p-6 sm:p-8 space-y-4" style={{ borderColor: BORDER, background: BG }}>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nome e cognome *"><Input data-testid="funnel-name" value={form.name} onChange={set("name")} className="border-white/15 text-white rounded-none focus-visible:ring-[#6E93C7]" style={{ background: BG_DARK }} placeholder="Mario Rossi" /></Field>
                <Field label="Azienda"><Input data-testid="funnel-company" value={form.company} onChange={set("company")} className="border-white/15 text-white rounded-none focus-visible:ring-[#6E93C7]" style={{ background: BG_DARK }} placeholder="La tua azienda" /></Field>
                <Field label="Telefono"><Input data-testid="funnel-phone" value={form.phone} onChange={set("phone")} className="border-white/15 text-white rounded-none focus-visible:ring-[#6E93C7]" style={{ background: BG_DARK }} placeholder="+39 ..." /></Field>
                <Field label="Email *"><Input data-testid="funnel-email" type="email" value={form.email} onChange={set("email")} className="border-white/15 text-white rounded-none focus-visible:ring-[#6E93C7]" style={{ background: BG_DARK }} placeholder="tu@email.it" /></Field>
              </div>
              <Field label="Tipo di richiesta">
                <Select value={form.interest} onValueChange={set("interest")}>
                  <SelectTrigger data-testid="funnel-interest" className="border-white/15 text-white rounded-none focus:ring-[#6E93C7]" style={{ background: BG_DARK }}><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent className="text-white border-white/15" style={{ background: BG_DARK }}>
                    <SelectItem value="Vendita commerciale">Voglio vendere un immobile commerciale</SelectItem>
                    <SelectItem value="Locazione commerciale">Voglio affittare / cercare un commerciale</SelectItem>
                    <SelectItem value="Valutazione">Voglio una valutazione gratuita</SelectItem>
                    <SelectItem value="Acquisto">Cerco un immobile da acquistare</SelectItem>
                    <SelectItem value="Residenziale">Immobile residenziale</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Messaggio"><Textarea data-testid="funnel-message" value={form.message} onChange={set("message")} rows={4} className="border-white/15 text-white rounded-none focus-visible:ring-[#6E93C7]" style={{ background: BG_DARK }} placeholder="Descrivi brevemente il tuo immobile o la tua esigenza..." /></Field>
              <label className="flex items-start gap-2.5 text-xs text-white/60 leading-relaxed cursor-pointer">
                <input type="checkbox" data-testid="funnel-privacy" checked={form.privacy}
                  onChange={(e) => setForm((f) => ({ ...f, privacy: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 accent-[#6E93C7] shrink-0" />
                <span>Autorizzo SIACASA a utilizzare i miei dati per rispondere a questa richiesta. Nessuna cessione a terzi. *</span>
              </label>
              <Button type="submit" disabled={sending} data-testid="funnel-submit"
                className="w-full h-12 text-base font-semibold rounded-none text-white transition-transform active:scale-[0.99] hover:brightness-110"
                style={{ background: ACCENT }}>
                {sending ? "Invio in corso..." : <>Invia richiesta <ArrowUpRight className="ml-2 h-4 w-4" /></>}
              </Button>
            </motion.form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t" style={{ background: BG, borderColor: BORDER }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid sm:grid-cols-3 gap-10 text-sm">
          <div>
            <Logo className="h-10" />
            <p className="text-white/45 mt-3 max-w-xs">Geom. Stefano Tronca — specialista dell'immobiliare commerciale in Brianza e Milano.</p>
          </div>
          <div className="space-y-2.5 text-white/70">
            <a href="tel:+390396840942" className="flex items-center gap-2 hover:text-white transition-colors"><Phone className="h-4 w-4" style={{ color: ACCENT }} /> 039 6840942 · 351 744 5275</a>
            <a href="mailto:commerciale@siacasaimmobiliare.it" className="flex items-center gap-2 hover:text-white transition-colors"><Mail className="h-4 w-4" style={{ color: ACCENT }} /> commerciale@siacasaimmobiliare.it</a>
            <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5" style={{ color: ACCENT }} /> Bellusco (MB) — Via Dante, 2</div>
            <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5" style={{ color: ACCENT }} /> Milano — Corso di Porta Vittoria, 46</div>
          </div>
          <div className="text-white/35 sm:text-right self-end">© {new Date().getFullYear()} SIACASA Immobiliare · siacasaimmobiliare.it</div>
        </div>
      </footer>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wide text-white/50 mb-1.5 block">{label}</span>
    {children}
  </label>
);
